import { Check, Copy } from 'lucide-react';
import type { RoutingSkuTargetRecord, RoutingTargetDisplayNamesResponse } from '@nadoutong/contracts';
import { useClipboard } from '../../hooks/useClipboard.tsx';
import { useT } from '../../i18n/index.ts';
import { Chip, IconButton, Tooltip } from '../../ui/index.ts';
import { LIVE_CAPABILITY_STATUSES, capabilityStatusLabel } from '../capabilityStatus.ts';

interface Props {
  readonly target: RoutingSkuTargetRecord;
  readonly names: RoutingTargetDisplayNamesResponse | undefined;
}

export function targetDisplayName(value: string | null | undefined, unnamed: string): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : unnamed;
}

export function targetSupplyLabel(
  target: RoutingSkuTargetRecord,
  names: RoutingTargetDisplayNamesResponse | undefined,
  unnamed: string,
): string {
  return [
    targetDisplayName(names?.upstream_names_by_id[target.upstream_id], unnamed),
    targetDisplayName(names?.key_bucket_labels_by_id[target.key_bucket_id], unnamed),
  ].join(' · ');
}

function NameValue({ value, className = 'text-paper' }: { readonly value: string; readonly className?: string }): JSX.Element {
  return (
    <Tooltip content={<span className="break-all">{value}</span>}>
      <span tabIndex={0} title={value} className={`block min-w-0 truncate ${className}`}>{value}</span>
    </Tooltip>
  );
}

/** Shared by manual and smart tables; these names never participate in a write. */
export function TenantRoutingTargetSupply({ target, names }: Props): JSX.Element {
  const t = useT();
  const unnamed = t('tenant_routing.supply.unnamed');
  const egress = names?.egress_by_target_id[target.id];
  const profileName = egress?.binding_found === false
    ? t('tenant_routing.supply.unbound')
    : targetDisplayName(egress?.egress_profile_name, unnamed);
  return (
    <dl
      className="m-0 grid min-w-0 grid-cols-[62px_minmax(0,1fr)] items-baseline gap-x-2 gap-y-0.5 text-dense"
      data-testid={`tenant-routing-supply-${target.id}`}
    >
      <dt className="text-micro text-paper-low">{t('tenant_routing.default.upstream')}</dt>
      <dd className="m-0 min-w-0 text-section font-semibold">
        <NameValue value={targetDisplayName(names?.upstream_names_by_id[target.upstream_id], unnamed)} />
      </dd>
      <dt className="text-micro text-paper-low">{t('tenant_routing.default.account')}</dt>
      <dd className="m-0 min-w-0">
        <NameValue className="text-paper-mid" value={targetDisplayName(names?.account_labels_by_id[target.account_id], unnamed)} />
      </dd>
      <dt className="text-micro text-paper-low">{t('tenant_routing.supply.key_bucket')}</dt>
      <dd className="m-0 min-w-0">
        <NameValue value={targetDisplayName(names?.key_bucket_labels_by_id[target.key_bucket_id], unnamed)} />
      </dd>
      <dt className="text-micro text-paper-low">{t('tenant_routing.supply.channel')}</dt>
      <dd className="m-0 min-w-0 font-mono">
        <NameValue className="text-paper-mid" value={targetDisplayName(target.upstream_channel_slug, unnamed)} />
      </dd>
      <dt className="text-micro text-paper-low">{t('tenant_routing.supply.egress')}</dt>
      <dd className="m-0 flex min-w-0 flex-wrap items-center gap-1">
        <span className="min-w-0 flex-1">
          <NameValue value={profileName} className={egress?.binding_found === false ? 'text-warn' : 'text-paper-mid'} />
        </span>
        {egress?.egress_profile_enabled === false ? (
          <Chip tone="warn" lamp={false}>{t('tenant_routing.supply.disabled')}</Chip>
        ) : null}
        {egress?.has_override === true ? (
          <Chip tone="warn" lamp={false}>{t('tenant_routing.supply.override')}</Chip>
        ) : null}
      </dd>
    </dl>
  );
}

function IdentityRow({ label, name, id }: { readonly label: string; readonly name: string; readonly id: string | null }): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  return (
    <div className="grid min-w-0 grid-cols-[44px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-x-2 gap-y-0.5">
      <dt className="text-micro text-paper-low">{label}</dt>
      <dd className="m-0 min-w-0 text-micro"><NameValue value={name} /></dd>
      <dd className="m-0 flex min-w-0 items-center gap-1">
        <span className="min-w-0 flex-1">
          <NameValue value={id ?? '—'} className="font-mono text-micro text-paper-low" />
        </span>
        {id !== null ? (
          <IconButton
            size="sm"
            label={t('tenant_routing.supply.copy_id', { label })}
            onClick={() => void copy(id)}
          >
            {copied ? <Check aria-hidden="true" className="h-3.5 w-3.5" /> : <Copy aria-hidden="true" className="h-3.5 w-3.5" />}
          </IconButton>
        ) : null}
      </dd>
      {copied || error !== null ? (
        <dd className="col-span-3 m-0 text-micro text-paper-mid" role="status">
          {copied ? t('common.copied') : t('tenant_routing.supply.copy_failed')}
        </dd>
      ) : null}
    </div>
  );
}

export function TenantRoutingTargetModel({ target, names }: Props): JSX.Element {
  const t = useT();
  const unnamed = t('tenant_routing.supply.unnamed');
  const egress = names?.egress_by_target_id[target.id];
  const model = targetDisplayName(target.upstream_model_name, unnamed);
  const identities = [
    [t('tenant_routing.default.upstream'), names?.upstream_names_by_id[target.upstream_id], target.upstream_id],
    [t('tenant_routing.default.account'), names?.account_labels_by_id[target.account_id], target.account_id],
    [t('tenant_routing.supply.key_bucket'), names?.key_bucket_labels_by_id[target.key_bucket_id], target.key_bucket_id],
    [t('tenant_routing.supply.channel'), target.upstream_channel_slug, target.upstream_channel_id],
    [t('tenant_routing.supply.egress'), egress?.binding_found === false ? t('tenant_routing.supply.unbound') : egress?.egress_profile_name, egress?.egress_profile_id ?? null],
    ...(target.capability_id == null ? [] : [
      [t('tenant_routing.default.capability'), target.upstream_model_name, target.capability_id] as const,
    ]),
    [t('tenant_routing.supply.target'), target.sku_name, target.id],
  ] as const;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dl className="m-0 grid min-w-0 grid-cols-[62px_minmax(0,1fr)] items-baseline gap-x-2 gap-y-0.5 text-dense">
        <dt className="text-micro text-paper-low">{t('tenant_routing.supply.upstream_model')}</dt>
        <dd className="m-0 min-w-0 font-mono" data-testid={`tenant-routing-pool-capability-${target.id}`}>
          <NameValue value={model} />
        </dd>
        <dt className="text-micro text-paper-low">{t('tenant_routing.supply.protocol')}</dt>
        <dd className="m-0 min-w-0 font-mono">
          <NameValue className="text-paper-mid" value={targetDisplayName(target.capability_protocol, unnamed)} />
        </dd>
      </dl>
      {target.capability_id == null ? (
        <Chip tone="warn" lamp={false}>{t('tenant_routing.target.capability_legacy')}</Chip>
      ) : !LIVE_CAPABILITY_STATUSES.has(target.capability_status ?? '') ? (
        <span className="block truncate text-micro text-warn">
          {capabilityStatusLabel(target.capability_status, t)}
        </span>
      ) : null}
      <details className="min-w-0" data-testid={`tenant-routing-pool-identity-${target.id}`}>
        <summary className="cursor-pointer text-micro text-paper-low hover:text-paper-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40">
          {t('tenant_routing.supply.details')}
        </summary>
        <dl className="m-0 mt-1 flex min-w-0 flex-col gap-2 rounded border border-line-soft bg-well p-2">
          {identities.map(([label, name, id]) => (
            <IdentityRow key={label} label={label} name={targetDisplayName(name, unnamed)} id={id} />
          ))}
        </dl>
      </details>
    </div>
  );
}

export function TenantRoutingSupplyLegend(): JSX.Element {
  const t = useT();
  return <p data-testid="tenant-routing-supply-legend" className="m-0 border-t border-line-soft px-4 py-2.5 text-label text-paper-low">{t('tenant_routing.supply.legend')}</p>;
}
